import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { SCANBACK_CHANGE } from 'containers/Dashboard/Home/constants'
import { selectWithoutScanback } from 'containers/Dashboard/Home/selectors'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../../PartnerOptions/constants'
import { shareResetError } from '../../../Share/actions'
import { LIST_TYPE } from '../constants'
import { selectListType } from '../List/selectors'

import {
  fetchAllFiltersError,
  fetchAllFiltersSuccess,
  fetchDataError,
  fetchDataSuccess,
  fetchMailboxAnomalyError,
  fetchMailboxAnomalySuccess,
  fetchMitigationDataError,
  fetchMitigationDataSuccess,
  fetchMostTargetedEmployeesError,
  fetchMostTargetedEmployeesSuccess,
  fetchTopProfilesError,
  fetchTopProfilesSuccess,
} from './actions'
import {
  DATA_REQUEST_ASYNC,
  DATE_FILTER_CHANGE,
  FETCH_ALL_FILTERS_ASYNC,
  MOST_ATTACKED_EMPLOYEES_REQUEST_ASYNC,
} from './constants'
import { selectDateFilter } from './selectors'

const fetchDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      DATA_REQUEST_ASYNC.INITIATE,
      DATE_FILTER_CHANGE,
      SCANBACK_CHANGE
    )
    .filter((action) => {
      if (
        action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS ||
        action.type === SCANBACK_CHANGE
      )
        return matchURL(URL_CONST.dashboard.mitigation)
      return true
    })
    .mergeMap(() =>
      api.dashboard
        .getThreatProtection(
          store.getState().partnerOptions.selectedCompany?.value,
          getRangeFromDateFilter(selectDateFilter(store.getState())),
          selectWithoutScanback(store.getState()),
          { mitigation: false, mailboxAnomaly: false, topProfiles: false }
        )
        .mergeMap((response) =>
          Observable.of(fetchDataSuccess(response.response), shareResetError())
        )
        .catch((error) => Observable.of(epicsErrorHandler(fetchDataError, error)))
        .takeUntil(action$.ofType(DATA_REQUEST_ASYNC.CANCEL))
    )

const fetchTopProfilesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(DATA_REQUEST_ASYNC.SUCCESS, SCANBACK_CHANGE)
    .filter((action) => {
      if (action.type === SCANBACK_CHANGE) return matchURL(URL_CONST.dashboard.mitigation)
      return true
    })
    .mergeMap(() =>
      api.dashboard
        .getThreatProtection(
          store.getState().partnerOptions.selectedCompany?.value,
          getRangeFromDateFilter(selectDateFilter(store.getState())),
          selectWithoutScanback(store.getState()),
          { mitigation: false, mailboxAnomaly: false, topProfiles: true }
        )
        .mergeMap((response) =>
          Observable.of(fetchTopProfilesSuccess(response.response), shareResetError())
        )
        .catch((error) => Observable.of(epicsErrorHandler(fetchTopProfilesError, error)))
    )

const fetchMostTargetedEmployeesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      MOST_ATTACKED_EMPLOYEES_REQUEST_ASYNC.INITIATE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      DATE_FILTER_CHANGE,
      SCANBACK_CHANGE
    )
    .filter((action) => {
      if (
        action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS ||
        action.type === SCANBACK_CHANGE
      )
        return matchURL(URL_CONST.dashboard.mitigation)
      return true
    })
    .mergeMap(() =>
      api.dashboard
        .getMostTargetedEmployees(
          store.getState().partnerOptions.selectedCompany?.value,
          getRangeFromDateFilter(selectDateFilter(store.getState())),
          selectWithoutScanback(store.getState())
        )
        .mergeMap((response) =>
          Observable.of(fetchMostTargetedEmployeesSuccess(response?.response), shareResetError())
        )
        .catch((error) => Observable.of(epicsErrorHandler(fetchMostTargetedEmployeesError, error)))
        .takeUntil(action$.ofType(MOST_ATTACKED_EMPLOYEES_REQUEST_ASYNC.CANCEL))
    )

const fetchMitigationDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(DATA_REQUEST_ASYNC.SUCCESS, SCANBACK_CHANGE)
    .filter((action) => {
      if (action.type === SCANBACK_CHANGE) return matchURL(URL_CONST.dashboard.mitigation)
      return true
    })
    .mergeMap(() =>
      api.dashboard
        .getThreatProtection(
          store.getState().partnerOptions.selectedCompany?.value,
          getRangeFromDateFilter(selectDateFilter(store.getState())),
          selectWithoutScanback(store.getState()),
          { mitigation: true, mailboxAnomaly: false, topProfiles: false }
        )
        .mergeMap((response) =>
          Observable.of(fetchMitigationDataSuccess(response.response), shareResetError())
        )
        .catch((error) => Observable.of(epicsErrorHandler(fetchMitigationDataError, error)))
    )

const fetchMailboxAnomalyEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(DATA_REQUEST_ASYNC.SUCCESS, SCANBACK_CHANGE)
    .filter((action) => {
      if (action.type === SCANBACK_CHANGE) return matchURL(URL_CONST.dashboard.mitigation)
      return true
    })
    .mergeMap(() =>
      api.dashboard
        .getThreatProtection(
          store.getState().partnerOptions.selectedCompany?.value,
          getRangeFromDateFilter(selectDateFilter(store.getState())),
          selectWithoutScanback(store.getState()),
          { mitigation: false, mailboxAnomaly: true, topProfiles: false }
        )
        .mergeMap((response) =>
          Observable.of(fetchMailboxAnomalySuccess(response.response), shareResetError())
        )
        .catch((error) => Observable.of(epicsErrorHandler(fetchMailboxAnomalyError, error)))
    )

const fetchAllFiltersEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS, FETCH_ALL_FILTERS_ASYNC.INITIATE)
    .filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.mitigation)
      return true
    })
    .mergeMap(() => {
      const isBanners = selectListType(store.getState()) === LIST_TYPE.BANNERS
      return (
        isBanners
          ? api.dashboard.getThreatProtectionBannersFilters(
              store.getState().partnerOptions.selectedCompany?.value
            )
          : api.dashboard.getThreatProtectionIncidentsFilters(
              store.getState().partnerOptions.selectedCompany?.value
            )
      )

        .map((response) => fetchAllFiltersSuccess(isBanners ? response : response.response))
        .catch((error) => Observable.of(epicsErrorHandler(fetchAllFiltersError, error)))
    })

export default combineEpics(
  fetchDataEpic,
  fetchTopProfilesEpic,
  fetchMitigationDataEpic,
  fetchMailboxAnomalyEpic,
  fetchAllFiltersEpic,
  fetchMostTargetedEmployeesEpic
)
